import React, { useState, useEffect } from 'react';
import { certificatesApi } from '../../services/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Certificate, Download, Eye, Info } from '@phosphor-icons/react';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { toast } from 'sonner';

export default function ContractorConsolidatedPage() {
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');
  const [previewDate, setPreviewDate] = useState('');

  useEffect(() => { fetchDays(); }, []);

  useEffect(() => {
    return () => { if (previewUrl) window.URL.revokeObjectURL(previewUrl); };
  }, [previewUrl]);

  const fetchDays = async () => {
    try {
      const res = await certificatesApi.myCompanyConsolidatedDays();
      setDays(res.data || []);
    } catch {
      toast.error('Error al cargar las fechas disponibles');
    } finally {
      setLoading(false);
    }
  };

  const fmtDay = (d) => format(parseISO(d), "EEEE dd 'de' MMMM yyyy", { locale: es });

  const handlePreview = async (date) => {
    setBusy(date);
    setPreviewUrl('');
    setPreviewTitle(`Certificado consolidado — ${fmtDay(date)}`);
    setPreviewDate(date);
    setPreviewOpen(true);
    try {
      const res = await certificatesApi.myCompanyConsolidated({ date, download: false });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      setPreviewUrl(url);
    } catch (err) {
      const msg = err?.response?.status === 403
        ? 'Disponible a partir de las 14:00 hrs del día de la capacitación'
        : 'No se pudo generar el certificado';
      toast.error(msg);
      setPreviewOpen(false);
    } finally {
      setBusy('');
    }
  };

  const handleDownload = async (date) => {
    setBusy(date);
    try {
      const res = await certificatesApi.myCompanyConsolidated({ date, download: true });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `consolidado_${date.replace(/-/g, '')}.pdf`);
      document.body.appendChild(link);
      link.click(); link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Certificado descargado');
    } catch (err) {
      const msg = err?.response?.status === 403
        ? 'Disponible a partir de las 14:00 hrs del día de la capacitación'
        : 'No se pudo descargar el certificado';
      toast.error(msg);
    } finally {
      setBusy('');
    }
  };

  if (loading) {
    return (<div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2BBAA4]" /></div>);
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="contractor-consolidated-page">
      <div>
        <h1 className="text-2xl font-bold text-[#2B4B8C]">Certificados por día</h1>
        <p className="text-[#2B4B8C]/60">Descarga el certificado consolidado de tu empresa por cada día de capacitación</p>
      </div>

      <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-sm text-blue-700">
        <Info size={18} className="shrink-0 mt-0.5" />
        <span>Los resultados y certificados quedan disponibles a partir de las <strong>14:00 hrs</strong> del día de la capacitación.</span>
      </div>

      {days.length === 0 ? (
        <Card className="bg-white border-[#2B4B8C]/10">
          <CardContent className="text-center py-12 text-[#2B4B8C]/50" data-testid="no-days">
            Aún no hay certificados disponibles. Aparecerán aquí después de las 14:00 hrs del día de la capacitación.
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-white border-[#2B4B8C]/10">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="border-[#2B4B8C]/10 hover:bg-transparent">
                  <TableHead className="text-[#2B4B8C]/60">Día de capacitación</TableHead>
                  <TableHead className="text-[#2B4B8C]/60">Registros</TableHead>
                  <TableHead className="text-[#2B4B8C]/60 text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {days.map((d) => (
                  <TableRow key={d.date} className="border-[#2B4B8C]/5 hover:bg-[#f8fafc]" data-testid={`day-row-${d.date}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Certificate size={16} className="text-[#2BBAA4]" />
                        <span className="text-[#2B4B8C] font-medium capitalize">{fmtDay(d.date)}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className="bg-[#2B4B8C]/5 text-[#2B4B8C] border border-[#2B4B8C]/10">{d.total} registro{d.total !== 1 ? 's' : ''}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button variant="outline" size="sm" disabled={busy === d.date} onClick={() => handlePreview(d.date)} className="border-[#2B4B8C]/20 text-[#2B4B8C]" data-testid={`preview-${d.date}`}>
                          <Eye size={15} className="mr-1.5" /> Ver
                        </Button>
                        <Button size="sm" disabled={busy === d.date} onClick={() => handleDownload(d.date)} className="bg-[#2BBAA4] hover:bg-[#249b89] text-white" data-testid={`download-${d.date}`}>
                          <Download size={15} className="mr-1.5" /> PDF
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-5xl w-[95vw] p-0 bg-white overflow-hidden" data-testid="consolidated-preview-modal">
          <DialogHeader className="px-5 pt-4 pb-3 border-b border-[#2B4B8C]/10">
            <DialogTitle className="text-[#2B4B8C] flex items-center gap-2 capitalize">
              <Certificate size={20} className="text-[#2BBAA4]" />
              {previewTitle}
            </DialogTitle>
          </DialogHeader>
          <div className="bg-[#525659] h-[75vh] flex items-center justify-center">
            {previewUrl ? (
              <iframe src={previewUrl} title="Vista previa" className="w-full h-full border-0" data-testid="consolidated-preview-iframe" />
            ) : (
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#2BBAA4]" />
            )}
          </div>
          <div className="flex justify-end gap-2 px-5 py-3 border-t border-[#2B4B8C]/10">
            <Button variant="outline" onClick={() => setPreviewOpen(false)} className="border-[#2B4B8C]/20 text-[#2B4B8C]">Cerrar</Button>
            <Button onClick={() => handleDownload(previewDate)} className="bg-[#2BBAA4] hover:bg-[#249b89] text-white" data-testid="modal-download-consolidated">
              <Download size={16} className="mr-1.5" /> Descargar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
